import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Paper, TextField, Button,
  Typography, Box, Link,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import OtpInput from '../components/OtpInput';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

function maskEmail(email) {
  if (!email) return 'your email';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const masked = local.length <= 2
    ? local
    : `${local.slice(0, 2)}${'*'.repeat(Math.max(1, local.length - 3))}${local.slice(-1)}`;
  return `${masked}@${domain}`;
}

function formatCountdown(s) {
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

const Card = ({ children }) => (
  <Container component="main" maxWidth="xs" sx={{ px: 2 }}>
    <Box sx={{ marginTop: { xs: '32px', sm: '64px' }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
        <Typography component="h1" variant="h4" align="center" gutterBottom>
          Pich Jewels
        </Typography>
        {children}
      </Paper>
    </Box>
  </Container>
);

const ErrorText = ({ msg }) =>
  msg ? <Typography variant="body2" color="error" sx={{ mb: 1.5, fontWeight: 500 }}>{msg}</Typography> : null;

const PasswordRulesChecklist = ({ password, touched, rules }) => (
  <Box sx={{ mb: 2 }}>
    {rules.map((rule) => {
      const pass = rule.test(password);
      const color = !touched ? 'text.secondary' : pass ? 'success.main' : 'error.main';
      const Icon = pass && touched ? CheckCircleOutlineIcon : CancelOutlinedIcon;
      return (
        <Box key={rule.label} display="flex" alignItems="center" gap={0.75} mb={0.25}>
          <Icon sx={{ fontSize: 16, color }} />
          <Typography variant="caption" sx={{ color }}>
            {rule.label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const ForgotPassword = () => {
  const { t } = useTranslation();

  const PASSWORD_RULES = [
    { label: t('password.minLength'), test: (p) => p.length >= 8 },
    { label: t('password.upperCase'), test: (p) => /[A-Z]/.test(p) },
    { label: t('password.lowerCase'), test: (p) => /[a-z]/.test(p) },
    { label: t('password.number'), test: (p) => /\d/.test(p) },
    { label: t('password.special'), test: (p) => /[!@#$%^&*()\-_=+[\]{};':",.<>/?`~\\|]/.test(p) },
  ];

  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [resetSessionToken, setResetSessionToken] = useState('');
  const [resetVerifiedToken, setResetVerifiedToken] = useState('');

  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const [resendEnabled, setResendEnabled] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const hasOtpError = !!error && step === 'verify';
  const otpCode = digits.join('');

  const resetCountdown = useCallback(() => {
    setCountdown(RESEND_COOLDOWN);
    setResendEnabled(false);
  }, []);

  useEffect(() => {
    if (step !== 'verify') return;
    resetCountdown();
  }, [step, resetCountdown]);

  useEffect(() => {
    if (step !== 'verify' || resendEnabled) return;
    if (countdown <= 0) { setResendEnabled(true); return; }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, countdown, resendEnabled]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/forgot-password', { email });
      setResetSessionToken(data.resetSessionToken || '');
      setStep('sent');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!resendEnabled || resendLoading) return;
    setError('');
    setResendLoading(true);
    try {
      const { data } = await axios.post('/api/auth/forgot-password/resend', { resetSessionToken });
      setResetSessionToken(data.resetSessionToken || resetSessionToken);
      setDigits(Array(OTP_LENGTH).fill(''));
      resetCountdown();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not resend code.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/forgot-password/verify-code', {
        resetSessionToken,
        code: otpCode,
      });
      setResetVerifiedToken(data.resetVerifiedToken);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordTouched(false);
      setStep('newPassword');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
      setDigits(Array(OTP_LENGTH).fill(''));
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setPasswordTouched(true);
    if (newPassword !== confirmPassword) { setError(t('password.noMatch')); return; }
    const allPass = PASSWORD_RULES.every((r) => r.test(newPassword));
    if (!allPass) { setError(t('password.requirements')); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/reset-password', { resetVerifiedToken, newPassword });
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.error || t('password.failedUpdate'));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'email') {
    return (
      <Card>
        <Typography variant="h6" fontWeight={600} gutterBottom>{t('auth.forgotPasswordTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('auth.forgotPasswordSubtitle')}
        </Typography>
        <ErrorText msg={error} />
        <Box component="form" onSubmit={handleSendCode}>
          <TextField
            fullWidth required margin="normal"
            label={t('auth.emailPlaceholder')} type="email" autoComplete="email" autoFocus
            value={email} onChange={(e) => setEmail(e.target.value)}
            helperText={t('auth.emailHelperText')}
          />
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 1 }} disabled={loading}>
            {loading ? t('auth.sendingCode') : t('auth.requestPasswordReset')}
          </Button>
          <Button fullWidth variant="outlined" component={RouterLink} to="/login">{t('auth.cancel')}</Button>
        </Box>
      </Card>
    );
  }

  if (step === 'sent') {
    return (
      <Card>
        <Typography variant="h6" fontWeight={600} gutterBottom>{t('auth.codeSentTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.codeSentBody')}
        </Typography>
        <ErrorText msg={error} />
        <Button
          fullWidth variant="contained" sx={{ mb: 1.5 }}
          onClick={() => setStep('verify')}
        >
          {t('auth.enterVerificationCode')}
        </Button>
        <Button
          fullWidth variant="outlined" sx={{ mb: 1.5 }}
          onClick={async () => {
            setError('');
            try {
              const { data } = await axios.post('/api/auth/forgot-password/resend', { resetSessionToken });
              setResetSessionToken(data.resetSessionToken || resetSessionToken);
              setDigits(Array(OTP_LENGTH).fill(''));
              setStep('verify');
              resetCountdown();
            } catch (err) {
              setError(err.response?.data?.error || 'Could not resend code.');
            }
          }}
        >
          {t('auth.resendVerificationCode')}
        </Button>
        <Button fullWidth variant="text" onClick={() => { setStep('email'); setError(''); }}>
          {t('auth.changeEmailAddress')}
        </Button>
      </Card>
    );
  }

  if (step === 'verify') {
    return (
      <Card>
        <Typography variant="h6" fontWeight={600} gutterBottom>{t('auth.codeVerificationTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auth.enterOtpSentTo')}
        </Typography>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>{maskEmail(email)}</Typography>
        <Box component="form" onSubmit={handleVerifyCode}>
          <OtpInput
            digits={digits}
            onChange={(d) => { setDigits(d); setError(''); }}
            hasError={hasOtpError}
          />
          {hasOtpError && (
            <Typography variant="body2" color="error" sx={{ mb: 1, fontWeight: 500 }}>{error}</Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'monospace', fontSize: '1rem', textAlign: 'center' }}>
            {formatCountdown(countdown)}
          </Typography>
          <Button
            type="submit" fullWidth variant="contained" sx={{ mb: 1.5 }}
            disabled={loading || otpCode.length !== OTP_LENGTH}
          >
            {loading ? t('auth.verifying') : t('auth.verifyCode')}
          </Button>
          <Button
            fullWidth variant="outlined"
            disabled={!resendEnabled || resendLoading}
            onClick={handleResendCode}
          >
            {resendLoading ? t('auth.sendingCode') : t('auth.resendCode')}{!resendEnabled && !resendLoading ? ` (${formatCountdown(countdown)})` : ''}
          </Button>
          <Box sx={{ textAlign: 'center', mt: 1.5 }}>
            <Link component="button" type="button" variant="body2" color="text.secondary"
              onClick={() => { setStep('sent'); setError(''); }}>
              {t('auth.back')}
            </Link>
          </Box>
        </Box>
      </Card>
    );
  }

  if (step === 'newPassword') {
    return (
      <Card>
        <Typography variant="h6" fontWeight={600} gutterBottom>{t('auth.newCredentialsTitle')}</Typography>
        <PasswordRulesChecklist password={newPassword} touched={passwordTouched} rules={PASSWORD_RULES} />
        <ErrorText msg={error} />
        <Box component="form" onSubmit={handleSetPassword}>
          <TextField
            fullWidth required margin="normal"
            label={t('auth.newPassword')} type="password" autoFocus
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setPasswordTouched(true); setError(''); }}
          />
          <TextField
            fullWidth required margin="normal"
            label={t('auth.confirmPassword')} type="password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
            error={!!confirmPassword && confirmPassword !== newPassword}
            helperText={confirmPassword && confirmPassword !== newPassword ? t('auth.passwordMismatch') : ''}
          />
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 1 }} disabled={loading}>
            {loading ? t('auth.updating') : t('auth.submit')}
          </Button>
          <Button fullWidth variant="outlined" component={RouterLink} to="/login">{t('auth.cancel')}</Button>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <Box textAlign="center" py={2}>
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" fontWeight={600} gutterBottom>{t('auth.passwordUpdatedTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.passwordUpdatedBody')}
        </Typography>
        <Button fullWidth variant="contained" component={RouterLink} to="/login">{t('auth.login')}</Button>
      </Box>
    </Card>
  );
};

export default ForgotPassword;
